import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

const DEBOUNCE_MS = 800;

/**
 * Isolated search input with local state so typing does not re-render the parent.
 * Commits to parent on: Enter key or Search button click, and (when autoSearch
 * is on, the default) a debounce 800ms after typing stops (if length >= 3 or
 * the box is emptied). With autoSearch off, nothing is searched until Enter or
 * the Search button.
 */
export default function SearchField({
  onCommit,
  loading = false,
  placeholder = "Search by strategy, symbol, ISIN, ticker, broker...",
  debounceMs = DEBOUNCE_MS,
  onInputChange = () => {},
  ariaLabel = "Search historical trades",
  hideButton = false,
  disabled = false,
  autoSearch = true,
}) {
  const [localValue, setLocalValue] = useState("");
  const debounceTimerRef = useRef(null);

  const commit = useCallback(
    (value) => {
      const trimmed = value?.trim() ?? "";
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      onCommit(trimmed);
    },
    [onCommit],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalValue(value);
      onInputChange(value);
      if (!autoSearch) return;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (value.trim().length === 0) {
        commit("");
        return;
      }

      if (value.trim().length >= 3) {
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          commit(value);
        }, debounceMs);
      }
    },
    [autoSearch, commit, debounceMs, onInputChange],
  );

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit(localValue);
      }
    },
    [commit, localValue],
  );

  const handleSearchClick = useCallback(() => {
    commit(localValue);
  }, [commit, localValue]);

  return (
    <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        aria-label={ariaLabel}
        disabled={disabled}
      />
      {!hideButton && (
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSearchClick}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
          sx={{
            minWidth: "100px",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "Searching..." : "Search"}
        </Button>
      )}
    </Box>
  );
}
